import {Component, OnInit, ViewChild, ElementRef, OnDestroy} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Location } from '@angular/common';
import { FormControl } from '@angular/forms';
import { MdDialogRef, MdDialog, MdDialogConfig, MdSnackBar } from '@angular/material';
import { NavigationService } from '@services/navigation-service';
import { DialogsService } from '@services/dialogs-service';
import { NegociosService, ENTREGA_VALUES } from '@services/negocios-service';
import { EntregaDialogComponent } from './entrega-dialog.component';
import { PATH_PRECIOS } from '../../../navigation/navigation.module';
import { MonedaService } from '@services/moneda-service';
import {PosicionService, EntregaService, SpinnerService, BreakpointsObserverService, CommonService} from '@services/index';
import { SignalrService } from '../../../signalr/signalR.service';
import { EntregasOcultasDialogComponent } from './entregas-ocultas-dialog.component';
import { ProductoService } from '@services/producto-service';
import { PreciosService } from '@services/precios-service';
import { DateTimePipe } from '@utils/pipes';
import { PermisosService, PERM_ACCIONES_ID } from '@services/permisos-service';
import {PageHeaderComponent} from '@common/page-header';
import {NegocioListComponent} from '../../negocios';
import {Subject} from 'rxjs/Subject';
import * as moment from 'moment';

@Component({
  selector: 'app-new-precio',
  templateUrl: './new-precio.component.html',
  styleUrls: ['./new-precio.component.scss']
})
export class NewPrecioComponent implements OnInit, OnDestroy {

  @ViewChild('tableHeader') tableHeader: ElementRef;
  @ViewChild('priceInput') priceInput: ElementRef;
  @ViewChild(NegocioListComponent) negociosComponent: NegocioListComponent;
  @ViewChild(PageHeaderComponent) pageHeader: PageHeaderComponent;

  public priceStateControl: FormControl;

  columns = ['entrega', 'moneda'];
  productColumns = [];
  // Columnas que usan un título no deducible desde su id en this.columns
  needSpecialName = {};

  rows = [];

  // Cuántos precios cargamos?
  pricesLoaded = 0;

  // Datos de la fila y columna actual donde estemos cargando datos
  currentPriceData: any = {};

  // Mostrar / ocultar tabla de simulación
  simulation = false;
  simulationPayload: any = [];

  submitting = false;

  monedas = [];
  productoOptions = [];
  posicionOptions = [];
  entregaOptions = [];
  entregaPosicionOptions = [];
  firstDayMonthTime;
  tnsTO;

  hiddenRows = [];

  negociosToUpdate = [];

  entregaIDs = {
    Entregado: 1,
    A_Entregar: 2
  };

  crearPosicionAllowed = false;

  tipoMercadoSelected: string = null;
  mercadoOptions = [];

  destroySubject = new Subject();
  isMobile = false;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private location: Location,
    private navService: NavigationService,
    private dialogsService: DialogsService,
    private negociosService: NegociosService,
    private productoService: ProductoService,
    private monedaService: MonedaService,
    private posicionService: PosicionService,
    private entregaService: EntregaService,
    private spinnerService: SpinnerService,
    private dialog: MdDialog,
    private snackBar: MdSnackBar,
    private signalRService: SignalrService,
    private preciosService: PreciosService,
    private datePipe: DateTimePipe,
    private permisosService: PermisosService,
    private breakpointsService: BreakpointsObserverService,
    private commonService: CommonService,
  ) {
    // usamos un ancho personalizado para este caso dado que estamos basando el responsive en una grilla de bootstrap
    // y el ancho de la grilla no condice con los breakpoints que usa google
    breakpointsService.observe(['(max-width: 991px)']).subscribe(x => {
      this.isMobile = x.matches;
    });
    this.permisosService.verificarAccesoDeURL();
    this.crearPosicionAllowed = this.permisosService.permisoAutorizado(PERM_ACCIONES_ID.A_CrearPrecio_CrearPosicion);
    this.spinnerService.setShowFlag(true);

    this.priceStateControl = new FormControl();

    this.firstDayMonthTime = moment().startOf('month').endOf('day').unix();

    monedaService.Options.takeUntil(this.destroySubject).subscribe(newValue => {
      this.monedas = newValue;
      this.getRows();
    });

    productoService.Options.takeUntil(this.destroySubject).subscribe(newValue => {
      this.productoOptions = newValue;
      this.productColumns = newValue.map(p => p.Producto.toLowerCase());
      this.getRows();
    });

    posicionService.Options.takeUntil(this.destroySubject).subscribe(newValue => {
      this.posicionOptions = newValue;
    });

    entregaService.Options.takeUntil(this.destroySubject).subscribe(newValue => {
      this.entregaOptions = newValue;
    });

    let posicionBackup = [];
    entregaService.PosicionOptions
      .switchMap(newValue => {
        posicionBackup = newValue;
        return this.commonService.getFirstBusinessDay(moment());
      })
      .takeUntil(this.destroySubject)
      .subscribe(firstDayMonth => {
        // el tiempo minimo es el primer dia habil del mes actual o el dia actual, el numero mas alto.
        const minTime = Math.max(firstDayMonth.startOf('day').unix(), moment().startOf('day').unix());
        this.entregaPosicionOptions = posicionBackup.filter(x => {
          if (!x.EsPosicion) { return true; }
          // si no es mes actual, solo permitir posiciones futuras
          return (x.Time / 1000) >= minTime;
        });
        this.getRows();
      });

    preciosService.getMercadosPizarra().takeUntil(this.destroySubject).subscribe(x => {
      this.mercadoOptions = x;
      this.tipoMercadoSelected = this.mercadoOptions.length ? this.mercadoOptions[0].Id : null;
    });

  }

  ngOnDestroy(): void {
    this.destroySubject.next();
    this.destroySubject.complete();
  }

  getRows() {
    if (!this.monedas || !this.entregaPosicionOptions || !this.productoOptions) { return; }
    this.rows = [];
    this.entregaPosicionOptions.forEach(e => {
      this.monedas.forEach(mon => {
        const newRow = {
          entrega_Id: e.Tipo === 'Entrega' ? e.ID : e.Entrega_Id,
          entrega: e.Entrega || e.Posicion,
          moneda: mon.ID,
          inputVisibilities: {},
          time: moment.unix(e.Time / 1000).startOf('day').unix(),
          Id_Pos: e.ID_POS ? e.ID_POS : null,
          EsPagoDiferido: e.EsPagoDiferido
        };
        this.productoOptions.forEach(prod => {
          newRow[prod.Producto.toLowerCase()] = {
            value: true
          };
        });
        this.rows.push(newRow);
      });
    });
    this.sortRows();
    this.getHiddenRows();
  }

  sortRows() {
    const order = [ENTREGA_VALUES.ENTREGADO, ENTREGA_VALUES.A_ENTREGAR].concat(this.posicionService.MonthOptions.map(m => m.ShortName));
    this.rows.sort((a, b) => {

      // preparamos los nombres de meses
      const namesA = a.entrega.split('-');
      const namesB = b.entrega.split('-');

      // estamos comparando entregas iguales
      if (a.entrega_Id === b.entrega_Id) {

        // estamos ordenando entregado y a estregar, ordenar por moneda
        if (a.entrega_Id === 1 || a.entregaIDs === 2) {
          return a.moneda - b.moneda;
        }

        // estamos ordenando N posiciones, ordenar por mes
        if (a.entrega_Id === 3) {

          // si la entrega es igual, ordenamos por moneda
          if (a.entrega === b.entrega) {
            return a.moneda - b.moneda;
          }

          // estamos comparando años iguales, ordenar segun mes
          if (namesA[1] === namesB[1]) {
            return order.indexOf(namesA[0]) - order.indexOf(namesB[0]);

            // estamos comparando años distintos, ordenar segun año
          } else {
            return Number(namesA[1]) - Number(namesB[1]);
          }
        }

        // estamos ordenando pago diferido, ordenar por Time
        if (a.entrega_Id === 4) {
          return a.Time - b.Time;
        }

        return 0;

      } else {
        // estamos comparando entregas distintas, ordenar por entrega
        return a.entrega_Id - b.entrega_Id;
      }
    });
    this.spinnerService.setShowFlag(false);
  }

  ngOnInit() {
    if (!this.monedaService.IsInitialized) { this.monedaService.loadOptions(); }
    if (!this.entregaService.IsInitialized) { this.entregaService.loadOptions(); }
    if (!this.posicionService.IsInitialized) { this.posicionService.loadOptions(); }
    if (!this.productoService.IsInitialized) { this.productoService.loadOptions(); }
  }

  navigateBack() {
    if (this.pricesLoaded > 0) {
      this.dialogsService.confirm(null,
        '¿Está seguro de salir sin cargar estos precios?',
        'SÍ, ACEPTAR',
        'NO, CANCELAR')
        .subscribe(res => {
          if (res) {
            this.pageHeader.catchEnded();
          }
        });
    } else {
      this.pageHeader.catchEnded();
    }
  }

  onScrollTable(e) {
    this.tableHeader.nativeElement.scrollLeft = e.target.scrollLeft;
  }

  getColumnTitle(col) {
    return this.needSpecialName[col] ||
      col.split('-').map(w => w.length ? w[0].toUpperCase() + w.slice(1) : '').join(' ');
  }

  onNewEntrega() {
    let dialogRef: MdDialogRef<EntregaDialogComponent>;

    const config = new MdDialogConfig();
    config.width = '380px';
    config.height = '400px';

    dialogRef = this.dialog.open(EntregaDialogComponent, config);
    dialogRef.componentInstance.hiddenRows = this.hiddenRows;
    // dialogRef.componentInstance.entregasInUse = this.getEntregasInUse();

    dialogRef.afterClosed().subscribe(res => {
      if (res) {
        this.updateRows();
      }
    });
  }

  getEntregasInUse() {
    const res = {
      'DISPONIBLE': {
        [ENTREGA_VALUES.ENTREGADO]: this.rows.filter(r => r.entrega === ENTREGA_VALUES.ENTREGADO).map(r => r.moneda),
        [ENTREGA_VALUES.A_ENTREGAR]: this.rows.filter(r => r.entrega === ENTREGA_VALUES.A_ENTREGAR).map(r => r.moneda)
      },
      'FORWARD': {},
      'BASIS': {
        'ARG': {},
        'CHI': {}
      }
    };
    this.rows.filter(r => r.entrega !== ENTREGA_VALUES.ENTREGADO && r.entrega !== ENTREGA_VALUES.A_ENTREGAR && !r.basis).forEach(r => {
      res['FORWARD'][r.entrega] = res['FORWARD'][r.entrega] || [];
      res['FORWARD'][r.entrega].push(r.moneda);
    });
    this.rows.filter(r => r.basis).forEach(r => {
      res['BASIS']['CHI'][r.entregaChi] = res['BASIS']['CHI'][r.entregaChi] || [];
      res['BASIS']['ARG'][r.entregaArg] = res['BASIS']['ARG'][r.entregaArg] || [];
      res['BASIS']['CHI'][r.entregaChi].push(r.entregaArg);
      res['BASIS']['ARG'][r.entregaArg].push(r.entregaChi);
    });

    return res;
  }

  displayPriceInput(row, col) {
    row.inputVisibilities[col] = true;
    row.tmp = row[col].value === true ? '' : row[col].value;
    row.error = false;
    const { EntregaId, PosicionId } = this.getEntregaAndPosicionForRow(row);
    this.currentPriceData = {
      EntregaId,
      PosicionId,
      MonedaId: row.moneda,
      ProductoId: this.productoOptions.find(p => p.Producto.toLowerCase() === col).ID
    };
    setTimeout(() => {
      this.priceInput.nativeElement.focus();
    }, 100);
  }

  onPriceInput(e, row, col) {
    clearTimeout(this.tnsTO);
    if (e.key === 'Escape') {
      row.inputVisibilities[col] = false;
    } else if (e.key === 'Enter') {
      this.savePriceInput(row, col);
    } else {
      this.tnsTO = setTimeout(_ => {
        this.searchMatchedTons(row, col);
      }, 500);
    }
  }

  searchMatchedTons(row, col) {
    if (row.tmp.length) {
      this.preciosService.getTonsByPrice({ ...this.currentPriceData, Precio: Number(row.tmp), MercadoId: this.tipoMercadoSelected})
        .subscribe(res => {
          row[col].matchedTons = {
            isValid: true,
            value: res.ResponseData
          };
        });
    } else {
      row[col].matchedTons = {
        isValid: false
      };
    }
  }

  savePriceInput(row, col) {
    if (!row.tmp.length) {
      // Si tenía cargado algo y lo borramos, restamos uno al contador de precios cargados
      if (row[col].value !== true) {
        this.pricesLoaded -= 1;
        this.simulation = false;
      }
      row[col].value = true;
      row.inputVisibilities[col] = false;
    } else {
      row.tmp = row.tmp.replace(',', '.');
      if (Number.isNaN(Number(row.tmp))) {
        row.error = true;
      } else {
        if (row[col].value !== row.tmp) {
          this.simulation = false;
        }
        if (row[col].value === true) {
          this.pricesLoaded += 1;
        }
        row[col].value = row.tmp;
        row.inputVisibilities[col] = false;
      }
    }
  }

  onPriceOver(row) {
    row.visibilityButton = true;
  }

  onPriceOut(row) {
    row.visibilityButton = false;
  }

  removeEntrega(i) {
    // Restar, del contador de precios cargados, los de esta fila
    // Si tiene precios cargados, se eliminan al ocultar la fila
    const tmp = this.pricesLoaded;
    this.productColumns.forEach(col => {
      this.pricesLoaded -= typeof this.rows[i][col].value === 'string' ? 1 : 0;
      if (typeof this.rows[i][col].value === 'string') {
        delete this.rows[i][col].matchedTons;
        this.rows[i][col].value = true;
        this.rows[i].inputVisibilities = {};
      }
    });

    // Si esta fila tenía precios cargados la simulación actual ya no es válida

    if (tmp > this.pricesLoaded) {
      this.simulation = false;
    }

    // preparar objeto y enviarlo, cuando se reciba una respuesta ocultar la fila
    const row = [{
      Id: this.rows[i].Id_Pos,
      Moneda: this.rows[i].moneda,
      // para tener consistencia, vamos a usar moment al enviar y recibir el time
      Fecha: moment.unix(this.rows[i].time).startOf('day').unix(),
      Hidden: true
    }];

    this.posicionService.updateRows(row)
      .subscribe(x => {
        if (x) {
          this.getHiddenRows();
        }
      });
  }

  getEntregaAndPosicionForRow(row) {
    let EntregaId = this.entregaOptions.find(e => e.Entrega === ENTREGA_VALUES.N_POSICIONES).ID;
    let PosicionId = null;

    if (row.entrega === ENTREGA_VALUES.ENTREGADO || row.entrega === ENTREGA_VALUES.A_ENTREGAR) {
      EntregaId = this.entregaOptions.find(e => e.Entrega === row.entrega).ID;
    } else if (row.EsPagoDiferido) {
      EntregaId = row.entrega_Id;
      PosicionId = row.Id_Pos;
    } else {
      const mapping = this.negociosService.getMonthLabelsInverseMapping();
      const parts = row.entrega.split('-');
      const posicion = parts.length === 1 ? parts[0] : mapping[parts[0]] + '-' + parts[1];
      PosicionId = this.posicionOptions.find(e => e.Posicion === posicion).ID;
    }

    return { EntregaId, PosicionId };
  }

  buildNewPricesPayload() {
    const payload = [];

    const products = this.productoOptions;
    this.rows.forEach(row => {
      const { EntregaId, PosicionId } = this.getEntregaAndPosicionForRow(row);

      products.forEach(product => {
        const price: any = {
          PosicionChicagoId: null,
          EntregaId,
          PosicionId,
          MonedaId: row.moneda,
          MercadoId: this.tipoMercadoSelected
        };
        if (typeof row[product.Producto.toLowerCase()].value === 'string') {
          price.ProductoId = product.ID;
          price.Precio = Number(row[product.Producto.toLowerCase()].value);
          price.NegociosIds = this.getNegociosForPrice(price.EntregaId, price.PosicionId, price.ProductoId);
          payload.push(price);

        }
      });
    });

    return payload;
  }

  onSubmit() {
    if (!this.simulation) {
      return;
    }
    if (this.tipoMercadoSelected === null) {
      this.snackBar.open('Por favor, seleccione un Mercado para los precios', '', {
        duration: 2000,
      });
      return;
    }
    const self = this;
    if (this.pricesLoaded > 0 && !this.submitting) {
      this.submitting = true;
      const dialogText = this.getDialogText();
      this.dialogsService.confirm(
        '¿Está seguro que desea cargar estos precios?', dialogText,
        'SÍ, ACEPTAR',
        'NO, CANCELAR')
        .subscribe(res => {
          if (res) {
            this.spinnerService.setShowFlag(true);
            const payload = this.buildNewPricesPayload();

            this.preciosService.addPrecio(payload).subscribe(x => {
              this.spinnerService.setShowFlag(false);
              if (x) {
                this.snackBar.open('Los precios fueron enviados a validar exitosamente', '', {
                  duration: 2000,
                });
                this.router.navigateByUrl('/' + PATH_PRECIOS);
              }
            }, err => {
              this.spinnerService.setShowFlag(false);
              this.snackBar.open('Ocurrió un error al cargar los precios', '', {
                duration: 2000,
              });
            });
          } else {
            this.submitting = false;
          }
        },
        err => {
          this.submitting = false;
          this.snackBar.open('Ocurrió un error al enviar esta solicitud', '', {
            duration: 2000,
          });
        });
    } else if (this.pricesLoaded <= 0) {
      this.snackBar.open('Es necesario cargar al menos un precio para enviar', '', {
        duration: 2000,
      });
    }
  }

  simulatePrecios() {
    if (this.tipoMercadoSelected === null) {
      this.snackBar.open('Por favor, seleccione un Mercado para los precios', '', {
        duration: 2000,
      });
      return;
    }
    this.simulationPayload = this.buildNewPricesPayload();
    this.simulation = true;
  }

  // mostrar dialog para seleccionar que filas deben mostrarse
  showEntregasOcultasDialog() {
    let dialogRef: MdDialogRef<EntregasOcultasDialogComponent>;
    const config = new MdDialogConfig();
    config.width = '400px';
    config.height = '400px';
    const order = [ENTREGA_VALUES.ENTREGADO, ENTREGA_VALUES.A_ENTREGAR].concat(this.posicionService.MonthOptions.map(m => m.ShortName));

    dialogRef = this.dialog.open(EntregasOcultasDialogComponent, config);
    dialogRef.componentInstance.hiddenRows = this.hiddenRows;
    dialogRef.componentInstance.order = order;

    dialogRef.afterClosed().subscribe(res => {
      if (res) {
        this.updateRows();
      }
    });
  }

  // Buscar filas ocultas y quitarlas

  getHiddenRows() {
    this.posicionService.getHiddenRows()
      .subscribe(res => {
        this.hiddenRows = res;
        this.hiddenRows.forEach(x => {
          x.PosFecha = moment(x.PosFecha).startOf('day').unix();
        });
        this.hideRows();
      });
  }

  hideRows() {
    if (this.rows.length !== 0) {
      // se usa un for invertido para mantener la integridad al sacar rows
      for (let i = this.rows.length - 1; i > -1; i--) {
        this.hiddenRows.forEach(y => {
          if (this.rows[i].Id_Pos !== null) {
            if (this.rows[i].Id_Pos === y.PosId && this.rows[i].moneda === y.Moneda) {
              this.rows.splice(i, 1);
            }
          }
        });
      }
    }
  }

  // actualizar Filas
  updateRows() {
    this.monedaService.loadOptions();
    this.posicionService.loadOptions();
    this.productoService.loadOptions();
    this.pricesLoaded = 0;
    this.simulation = false;
  }

  getDialogText() {
    this.negociosToUpdate = this.negociosComponent.negociosToUpdate();
    let text = '';
    if (this.negociosToUpdate.length) {
      text = 'Los siguientes negocios se van a actualizar:<br>';

      this.negociosToUpdate.forEach(x => {
        let line = '- ';
        line += x.Producto.Producto + ' ';
        line += x.Toneladas.toString() + ' Tn ';
        line += x.TipoNegocio.Tipo + ' ';
        line += (x.Entrega.EsPosicion ? this.getPosicionStringFormat(x.NegocioTipoPosicion.NegocioTipoPosicion) : x.Entrega.Entrega) + ' ';
        line += x.Base.toString() + ' ';
        line += x.Moneda.Simbolo;
        text += line + '<br>';
      });
    }
    return text;
  }

  getPosicionStringFormat(posicion: string) {
    const parts = posicion.split('-');
    return parts[0].slice(0, 3).toUpperCase() + '-' + parts[1];
  }

  getTooltip() {
    return this.simulation ? 'Guardar' : 'Ejecutar una simulacion primero';
  }

  getNegociosForPrice(entrega, posicion, producto) {
    const list = [];
    if (this.negociosToUpdate.length) {
      this.negociosToUpdate.forEach(x => {
        if (x.Entrega.Id === this.entregaIDs.Entregado || x.Entrega.Id === this.entregaIDs.A_Entregar) {
          if (x.Entrega.Id === entrega && x.Producto.Id === producto) {
            list.push(x.Id);
          }
        } else if (posicion !== null && x.NegocioTipoPosicion !== null) {
          if (posicion === x.NegocioTipoPosicion.Id && x.Producto.Id === producto) {
            list.push(x.Id);
          }
        }
      });
    }
    return list;
  }

  refreshSimulation() {
    this.simulation = false;
  }

}





// WEBPACK FOOTER //
// E:/jenkins_home/workspace/_agenda-unco-front-legacy_master/frontend-web/src/app/components/precios-seccion/new-precio/new-precio.component.ts